import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Crown, Star, Gem, Medal } from "lucide-react";
import { api } from "@/lib/api";
import { dateOnly } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";

export const PromoList = ({ testid = "promotions-list" }) => {
  const [promos, setPromos] = useState(null);
  useEffect(() => { api.get("/promotions").then((r) => setPromos(r.data)).catch(() => setPromos([])); }, []);
  if (!promos) return <Skeleton className="h-64 bg-cr-elevated" />;
  return (
    <div data-testid={testid} className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {promos.map((p) => (
        <article key={p.id} data-testid={`promotion-${p.id}`} className="card-cr overflow-hidden">
          <img src={p.image} alt="" className="h-40 w-full object-cover" />
          <div className="p-5">
            <h3 className="font-display text-xl font-bold">{p.title}</h3>
            <p className="mt-1 text-sm text-cr-ink/80">{p.subtitle}</p>
            <dl className="mt-4 space-y-1 text-xs text-cr-muted">
              <div><dt className="inline font-semibold text-cr-ink">Eligibility: </dt><dd className="inline">{p.eligibility}</dd></div>
              <div><dt className="inline font-semibold text-cr-ink">Expires: </dt><dd className="inline">{dateOnly(p.expires_at)}</dd></div>
              <div><dt className="inline font-semibold text-cr-ink">Terms: </dt><dd className="inline">{p.terms}</dd></div>
            </dl>
            <Link to={p.cta_link} data-testid={`promotion-cta-${p.id}`} className="btn-gold mt-5 px-4 py-2 text-xs">{p.cta_label}</Link>
          </div>
        </article>
      ))}
    </div>
  );
};

export default function Promotions() {
  return (
    <main data-testid="promotions-page" className="mx-auto max-w-7xl px-5 py-10">
      <h1 className="text-4xl font-black uppercase">Promo<span className="gold-text">tions</span></h1>
      <p className="mb-8 mt-2 text-sm text-cr-muted">Current offers with eligibility, expiry and full terms.</p>
      <PromoList />
    </main>
  );
}

const TIERS = [[Medal, "Bronze", "Starting tier for every verified member"], [Star, "Silver", "Priority ticket handling"], [Gem, "Gold", "Dedicated account manager"], [Crown, "Platinum", "Bespoke offers and events"]];

export function Vip() {
  const { user } = useAuth();
  return (
    <main data-testid="vip-page" className="mx-auto max-w-7xl px-5 py-10">
      <div className="relative overflow-hidden rounded-2xl border border-cr-gold/40 p-10">
        <img src="/brand/crown.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative"><h1 className="text-4xl font-black uppercase">VIP <span className="gold-text">Club</span></h1>
          <p className="mt-2 max-w-xl text-cr-ink/80">Climb the tiers for priority support, a dedicated manager and exclusive offers.</p>
          {user && <p data-testid="vip-current-tier" className="mt-4 text-sm">Your tier: <b className="text-cr-gold">{user.vip_tier}</b></p>}</div>
      </div>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {TIERS.map(([I, t, d]) => <div key={t} data-testid={`vip-tier-${t.toLowerCase()}`} className="card-cr p-6"><I className="h-9 w-9 text-cr-gold" /><p className="mt-4 font-display text-xl font-bold">{t}</p><p className="mt-1 text-sm text-cr-muted">{d}</p></div>)}
      </div>
    </main>
  );
}

const DOCS = {
  terms: ["Terms & Conditions", ["By using CRBETS you agree to these terms.", "Users must be 18+ and located in a permitted jurisdiction.", "Promotions, deposits and withdrawals are subject to their individual terms and identity verification."]],
  privacy: ["Privacy Policy", ["We collect account details (name, email, phone, date of birth, country) to operate your account.", "Passwords are stored as one-way hashes. Card details are never stored.", "You may request access to or deletion of your data via support."]],
  "responsible-gaming": ["Responsible Gaming", ["Set daily, weekly and monthly deposit limits under Account › Settings.", "Use cooling-off or self-exclusion to take a break; deposits are blocked during the period.", "If gaming stops being fun, contact support or a local problem-gambling helpline."]],
  "kyc-aml": ["KYC / AML Policy", ["Identity verification is required before any withdrawal.", "Transactions are monitored with risk checks and reviewed by staff.", "Suspicious activity may lead to account suspension pending review."]],
  complaints: ["Complaints Procedure", ["Raise a ticket with category 'Complaint' from Account › Support.", "We acknowledge complaints and track status in your ticket history.", "If you are not satisfied with our response you may escalate to our licensing authority."]],
};

export function Legal() {
  const { slug } = useParams();
  const { config } = useAuth();
  const docs = { ...DOCS, licensing: ["Licensing Information", [config?.compliance?.license_text || "CRBETS is a licensed operator."]] };
  const [title, paras] = docs[slug] || ["Not found", ["This page does not exist."]];
  return (
    <main data-testid={`legal-page-${slug}`} className="mx-auto max-w-3xl px-5 py-14">
      <h1 className="text-3xl font-bold">{title}</h1>
      <div className="mt-6 space-y-4 text-cr-ink/80">{paras.map((p) => <p key={p}>{p}</p>)}</div>
    </main>
  );
}

const FAQ = [
  ["What is the minimum deposit?", "The minimum deposit is configured by the operator (currently shown on the deposit page)."],
  ["How do withdrawals work?", "Withdrawals require a verified email and approved identity verification. Each request is reviewed by staff before it is completed."],
  ["How do I play the Golden Arcade?", "Your admin credits arcade coins to your account. Open Arcade, pick a game, choose your bet and play."],
  ["How do I set deposit limits?", "Go to Account › Settings › Responsible Gaming to set limits, a cooling-off period or self-exclusion."],
  ["How do I contact support?", "Open a ticket from Account › Support and track its status there."],
];

export function Faq() {
  return (
    <main data-testid="faq-page" className="mx-auto max-w-3xl px-5 py-14">
      <h1 className="text-3xl font-bold">Frequently Asked <span className="gold-text">Questions</span></h1>
      <Accordion type="single" collapsible className="mt-8 space-y-3">
        {FAQ.map(([q, a], i) => (
          <AccordionItem key={q} value={`f${i}`} className="card-cr px-5">
            <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left hover:no-underline">{q}</AccordionTrigger>
            <AccordionContent className="text-cr-ink/75">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </main>
  );
}
